import { useMutation, useQuery, UseQueryOptions, UseQueryResult } from "react-query"
import { APIEndpoints } from "../constants/api-endpoints"
import { HttpRequest } from "../services/_http"
import { QueryKeys } from "./_query_keys"

// Get all prompts with pagination support
export const useAIPrompts = (params?: {
    pageSize?: number
    pageIndex?: number
    search?: string
    sortId?: string
    sortDesc?: boolean
}, options?: UseQueryOptions): UseQueryResult<any> => {
    const method = 'GET'
    const queryKey = [QueryKeys.PROMPTS, params]
    
    return useQuery({
        queryKey,
        queryFn: async () => {
            const queryParams = new URLSearchParams()
            
            if (params?.pageSize) {
                queryParams.append('pagination[pageSize]', params.pageSize.toString())
            }
            if (params?.pageIndex !== undefined) {
                queryParams.append('pagination[pageIndex]', params.pageIndex.toString())
            }
            if (params?.search) {
                queryParams.append('search', params.search)
            }
            if (params?.sortId) {
                queryParams.append('sort[id]', params.sortId)
            }
            if (params?.sortDesc !== undefined) {
                queryParams.append('sort[desc]', params.sortDesc.toString())
            }
            
            const url = queryParams.toString() 
                ? `${APIEndpoints.Prompts}?${queryParams.toString()}`
                : APIEndpoints.Prompts
            
            const response: any = await HttpRequest({
                url,
                method
            })
            
            return response?.data?.results || []
        },
        refetchOnWindowFocus: false,
        ...options
    })
}

// Get active prompt (filter by status = 1)
export const useActiveAIPrompt = (options?: UseQueryOptions): UseQueryResult<any> => {
    const method = 'GET'
    const queryKey = [QueryKeys.PROMPTS, 'active']
    
    return useQuery({
        queryKey,
        queryFn: async () => {
            // Use field filter to get only active prompts
            const queryParams = new URLSearchParams()
            queryParams.append('fields[0][id]', 'status')
            queryParams.append('fields[0][value]', '1')
            
            const response: any = await HttpRequest({
                url: `${APIEndpoints.Prompts}?${queryParams.toString()}`,
                method
            })
            
            // Extract results array and find first active prompt
            const prompts = response?.data?.results || []
            return Array.isArray(prompts) && prompts.length > 0 ? prompts[0] : null
        },
        refetchOnWindowFocus: false,
        cacheTime: 0,
        ...options
    })
}

// Get single prompt by ID
export const useAIPrompt = (promptId: string, options?: UseQueryOptions): UseQueryResult<any> => {
    const method = 'GET'
    const queryKey = [QueryKeys.PROMPTS, promptId]
    
    return useQuery({
        queryKey,
        queryFn: async () => {
            const response: any = await HttpRequest({
                url: `${APIEndpoints.Prompts}/${promptId}`,
                method
            })
            return response?.data?.results || null
        },
        refetchOnWindowFocus: false,
        enabled: !!promptId,
        ...options
    })
}

// Create new prompt
export const useCreateAIPrompt = (options?: any) => {
    const method = 'POST'
    return useMutation(async ({data}: {data: any}) => {
        const response: any = await HttpRequest({
            url: APIEndpoints.Prompts,
            method,
            data
        })
        return response?.data?.results || response?.data
    }, options)
}

// Update prompt
export const useUpdateAIPrompt = (options?: any) => {
    const method = 'PUT'
    return useMutation(async ({prompt_id, data}: {prompt_id: string, data: any}) => {
        const response: any = await HttpRequest({
            url: `${APIEndpoints.Prompts}/${prompt_id}`,
            method,
            data
        })
        return response?.data?.results || response?.data
    }, options)
}

// Toggle prompt status (dedicated endpoint)
export const useTogglePromptStatus = (options?: any) => {
    const method = 'PATCH'
    return useMutation(async ({prompt_id, status}: {prompt_id: string, status: number}) => {
        const response: any = await HttpRequest({
            url: `${APIEndpoints.Prompts}/${prompt_id}/status`,
            method,
            data: { status }
        })
        return response?.data?.results || response?.data
    }, options)
}

// Delete prompt (soft delete)
export const useDeleteAIPrompt = (options?: any) => {
    const method = 'DELETE'
    return useMutation(async ({prompt_id}: {prompt_id: string}) => {
        const response: any = await HttpRequest({
            url: `${APIEndpoints.Prompts}/${prompt_id}`,
            method
        })
        return response?.data?.results || response?.data
    }, options)
}

// Get version history for a prompt
export const usePromptVersions = (promptId: string, params?: {
    pageSize?: number
    pageIndex?: number
}, options?: UseQueryOptions): UseQueryResult<any> => {
    const method = 'GET'
    const queryKey = [QueryKeys.PROMPTS, promptId, 'versions', params]

    return useQuery({
        queryKey,
        queryFn: async () => {
            const queryParams = new URLSearchParams()

            if (params?.pageSize) {
                queryParams.append('pagination[pageSize]', params.pageSize.toString())
            }
            if (params?.pageIndex !== undefined) {
                queryParams.append('pagination[pageIndex]', params.pageIndex.toString())
            }

            const url = queryParams.toString()
                ? `${APIEndpoints.Prompts}/${promptId}/versions?${queryParams.toString()}`
                : `${APIEndpoints.Prompts}/${promptId}/versions`

            const response: any = await HttpRequest({
                url,
                method
            })

            return response?.data?.results || []
        },
        refetchOnWindowFocus: false,
        enabled: !!promptId,
        ...options
    })
}

// Get specific version of a prompt
export const usePromptVersion = (promptId: string, versionNumber: number, options?: UseQueryOptions): UseQueryResult<any> => {
    const method = 'GET'
    const queryKey = [QueryKeys.PROMPTS, promptId, 'versions', versionNumber]

    return useQuery({
        queryKey,
        queryFn: async () => {
            const response: any = await HttpRequest({
                url: `${APIEndpoints.Prompts}/${promptId}/versions/${versionNumber}`,
                method
            })
            return response?.data?.results || null
        },
        refetchOnWindowFocus: false,
        enabled: !!promptId && !!versionNumber,
        ...options
    })
}

// Restore prompt to a specific version
export const useRestorePromptVersion = (options?: any) => {
    const method = 'POST'
    return useMutation(async ({prompt_id, version_number}: {prompt_id: string, version_number: number}) => {
        const response: any = await HttpRequest({
            url: `${APIEndpoints.Prompts}/${prompt_id}/versions/${version_number}/restore`,
            method
        })
        return response?.data?.results || response?.data
    }, options)
}

// Legacy hooks for backward compatibility
export const useAISettings = useActiveAIPrompt
export const useUpdateAISettings = useUpdateAIPrompt
export const useCreateAISettings = useCreateAIPrompt
